import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';

import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import LanguageSelector from '@/components/inputs/LanguageSelector';
import { useAuth } from '@/providers/auth/AuthProvider';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import { Box, Divider, FormControl, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Select, SelectChangeEvent, Stack, styled, Typography } from '@mui/material';
import { DashboardLayout, SidebarFooterProps, ThemeSwitcher } from '@toolpad/core/DashboardLayout';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useSchool, useSession } from '../providers/session/SessionProvider';

const NavigationListItemButton = styled(ListItemButton)(() => {
    return {
        borderRadius: 8,
    };
});

function FooterItem(props: { icon: React.ReactNode; title: string; mini: boolean; onClick?: () => void; ariaLabel?: string }) {
    return (
        <ListItem
            sx={{
                py: 0,
                px: 1,
                overflowX: 'hidden',
            }}
        >
            <NavigationListItemButton
                sx={{
                    px: 1.4,
                    height: 48,
                    minWidth: 68,
                    justifyContent: props.mini ? 'center' : undefined,
                }}
                onClick={props.onClick}
                aria-label={props.ariaLabel}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 34,
                        justifyContent: 'center',
                    }}
                >
                    {props.icon}
                </ListItemIcon>
                {!props.mini && (
                    <ListItemText
                        primary={props.title}
                        sx={{
                            ml: 1.2,
                            whiteSpace: 'nowrap',
                            '& .MuiTypography-root': {
                                fontWeight: '500',
                            },
                        }}
                    />
                )}
            </NavigationListItemButton>
        </ListItem>
    );
}

function SidebarFooter({ mini }: SidebarFooterProps) {
    const { t, i18n } = useTranslation();
    const { logout } = useAuth();
    const navigate = useNavigate();
    return (
        <Box>
            <Divider />
            <List sx={{ px: 0, py: 1 }}>
                <LanguageSelector
                    anchorComponent={(props) => <FooterItem title={t('common.language')} icon={<LanguageIcon />} mini={mini} {...props} />}
                    onChange={(lang) => i18n.changeLanguage(lang)}
                />
                <FooterItem
                    title={t('common.logout')}
                    icon={<LogoutIcon />}
                    onClick={() => logout().finally(() => navigate('/welcome', { replace: true }))}
                    mini={mini}
                    ariaLabel={t('common.logout')}
                />
            </List>
        </Box>
    );
}

function ToolbarActionsSchoolSelect() {
    const { t } = useTranslation();
    const { session } = useSession();
    const { school, setSchool } = useSchool();

    const handleChange = (event: SelectChangeEvent) => {
        const school = session?.user?.teacherProfile?.schools.find((school) => school.id === event.target.value);
        if (school) {
            setSchool(school);
        }
    };

    return (
        <Stack direction={'row'} spacing={1.5}>
            <FormControl sx={{ m: 1, width: '100%' }}>
                <InputLabel id='school-select-label'>{t('common.school')}</InputLabel>
                <Select
                    labelId='school-select-label'
                    id='school-select'
                    value={school?.id || ''}
                    label={t('common.school')}
                    onChange={handleChange}
                    renderValue={(selected) => {
                        const selectedSchool = session?.user?.teacherProfile?.schools.find((s) => s.id === selected);
                        if (!selectedSchool) return '';
                        const name = selectedSchool.schoolName || '';
                        const avatarColor = generateAvatarColor(name);
                        return (
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <InitialalsAvatar
                                    sx={{
                                        bgcolor: avatarColor?.bg,
                                        color: avatarColor?.text,
                                    }}
                                >
                                    {getInitials(name)}
                                </InitialalsAvatar>
                                <Typography variant='body2'>{name}</Typography>
                            </Stack>
                        );
                    }}
                >
                    {session?.user?.teacherProfile?.schools.map((school) => {
                        const name = school.schoolName || '';
                        const avatarColor = generateAvatarColor(name);
                        return (
                            <MenuItem key={school.id} value={school.id}>
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <InitialalsAvatar
                                        sx={{
                                            bgcolor: avatarColor?.bg,
                                            color: avatarColor?.text,
                                        }}
                                    >
                                        {getInitials(name)}
                                    </InitialalsAvatar>
                                    <Typography>{name}</Typography>
                                </Stack>
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
            <ThemeSwitcher />
        </Stack>
    );
}

export default function Layout() {
    const { session } = useSession();
    const location = useLocation();

    if (!session) {
        // Add the `callbackUrl` search parameter
        const redirectTo = `/welcome?callbackUrl=${encodeURIComponent(location.pathname)}`;

        return <Navigate to={redirectTo} replace />;
    }

    if (location.pathname.startsWith('/classrooms/')) {
        return <Outlet />;
    }

    return (
        <DashboardLayout
            defaultSidebarCollapsed
            slots={{
                toolbarActions: ToolbarActionsSchoolSelect,
                sidebarFooter: SidebarFooter,
            }}
            sx={{
                height: '100svh',
            }}
        >
            <Outlet />
        </DashboardLayout>
    );
}
